import React, { useState, useEffect, useCallback, useRef, useImperativeHandle } from "react";
import { useToast } from "../../../components/ui";
import { AssetTableItem } from "../../../types/types";
import { flattenAssets, collectAllIds, getSubtreeIds } from "./utils";
import AssetTreePanel from "./AssetTreePanel";
import AssetDetailPanel from "./AssetDetailPanel";
import AssetCreateModal from "./AssetCreateModal";
import AssetImportModal from "./AssetImportModal";
import { apiFetch } from "../../../services/apiClient";
import { useIsMobile } from "../../../hooks";

export interface AssetsExplorerViewHandle {
  openAddRoot: () => void;
  openImport: () => void;
  toggleSelectionMode: () => void;
  getSelectedAssets: () => AssetTableItem[];
  isSelectionMode: () => boolean;
}

const AssetsExplorerView = React.forwardRef<AssetsExplorerViewHandle>(
  function AssetsExplorerView(_props, ref) {
    const toast = useToast();

    const [assets, setAssets] = useState<AssetTableItem[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedAsset, setSelectedAsset] = useState<AssetTableItem | null>(null);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [filterText, setFilterText] = useState("");
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    // Create modal
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createModalParentId, setCreateModalParentId] = useState<number | null>(null);

    // Import modal
    const [importModalOpen, setImportModalOpen] = useState(false);

    // ── Selection mode (for Marketplace) ──────────────────────────────────────
    const [selectionMode, setSelectionMode] = useState(false);
    const [multiSelectedIds, setMultiSelectedIds] = useState<Set<number>>(new Set());

    // ── Drag-and-drop state ────────────────────────────────────────────────────
    const [draggedId, setDraggedId] = useState<number | null>(null);
    const [dropTargetId, setDropTargetId] = useState<number | null>(null);
    const [dropOverRoot, setDropOverRoot] = useState(false);
    const [invalidDropIds, setInvalidDropIds] = useState<Set<number>>(new Set());

    // ── Mobile tree drawer ────────────────────────────────────────────────────
    const isMobile = useIsMobile();
    const [treeDrawerOpen, setTreeDrawerOpen] = useState(false);

    // ── Resizable sidebar ─────────────────────────────────────────────────────
    const [sidebarWidth, setSidebarWidth] = useState(288); // default w-72 = 288px
    const isResizing = useRef(false);
    const resizeStartX = useRef(0);
    const resizeStartWidth = useRef(0);

    const handleResizeStart = (e: React.MouseEvent) => {
      isResizing.current = true;
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = sidebarWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    };

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isResizing.current) return;
        const delta = e.clientX - resizeStartX.current;
        const newWidth = Math.max(180, Math.min(520, resizeStartWidth.current + delta));
        setSidebarWidth(newWidth);
      };
      const handleMouseUp = () => {
        if (isResizing.current) {
          isResizing.current = false;
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        }
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, []);

    // ── Flat asset list for autocomplete / chip lookup ────────────────────────
    const flatAssets = flattenAssets(assets);

    // ── Fetch hierarchy ───────────────────────────────────────────────────────
    // `silent = true` skips the loading spinner so the sidebar doesn't flash
    // on background refreshes triggered by structural operations (reparent, etc.).
    const fetchHierarchy = useCallback(
      async (silent = false) => {
        if (!silent) setLoading(true);
        try {
          const res = await apiFetch("/api/protected/assets/hierarchy");
          if (!res.ok) throw new Error("Failed to load assets");
          const data: AssetTableItem[] = await res.json();
          setAssets(data ?? []);
          // Auto-expand first level
          setExpandedIds((prev) => {
            const next = new Set(prev);
            (data ?? []).forEach((a) => {
              if (a.children && a.children.length > 0) next.add(a.id);
            });
            return next;
          });
        } catch {
          toast.showToast({ type: "error", message: "Failed to load assets." });
        } finally {
          if (!silent) setLoading(false);
        }
      },
      [toast]
    );

    const handleToggleMultiSelect = useCallback((id: number) => {
      setMultiSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }, []);

    useEffect(() => {
      void fetchHierarchy();
    }, [fetchHierarchy]);

    // ── Fetch full asset detail on selection ──────────────────────────────────
    const loadDetail = useCallback(
      async (id: number) => {
        setDetailLoading(true);
        try {
          const res = await apiFetch(`/api/protected/assets/asset/${id}`);
          if (!res.ok) throw new Error("Asset not found");
          const data: AssetTableItem = await res.json();
          setSelectedAsset(data);
        } catch {
          toast.showToast({ type: "error", message: "Failed to load asset details." });
          setSelectedAsset(null);
        } finally {
          setDetailLoading(false);
        }
      },
      [toast]
    );

    useEffect(() => {
      if (selectedId != null) loadDetail(selectedId);
      else setSelectedAsset(null);
    }, [selectedId, loadDetail]);

    // ── Tree actions ──────────────────────────────────────────────────────────
    const handleSelect = (id: number) => {
      setSelectedId(id);
      setTreeDrawerOpen(false);
    };

    const handleToggleExpand = (id: number) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    };

    const handleExpandAll = () => {
      setExpandedIds(collectAllIds(assets));
    };

    const handleCollapseAll = () => setExpandedIds(new Set());

    const handleFilterChange = (text: string) => {
      setFilterText(text);
      if (text) setExpandedIds(collectAllIds(assets));
    };

    const handleAddChild = (parentId: number) => {
      setCreateModalParentId(parentId);
      setCreateModalOpen(true);
    };

    const handleAddRoot = () => {
      setCreateModalParentId(null);
      setCreateModalOpen(true);
    };

    // ── Delete asset ──────────────────────────────────────────────────────────
    const handleDelete = async (id: number) => {
      const res = await apiFetch(`/api/protected/assets/asset/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        if (selectedId === id) {
          setSelectedId(null);
          setSelectedAsset(null);
        }
        await fetchHierarchy();
        toast.showToast({ type: "success", message: "Asset deleted." });
      } else {
        toast.showToast({ type: "error", message: "Failed to delete asset." });
      }
    };

    // ── After inline field save: reload detail only (tree is unchanged) ────────
    const handleRefreshDetail = useCallback(async () => {
      if (selectedId != null) await loadDetail(selectedId);
    }, [selectedId, loadDetail]);

    // ── After inline edit (Name/Type/Category/Parent changed) ─────────────────
    // Optimistically update the detail pane, then silently refresh the tree so
    // the sidebar reflects the new name / hierarchy position.
    const handleEdit = useCallback(
      async (updated: AssetTableItem) => {
        setSelectedAsset(updated);
        await fetchHierarchy(true);
      },
      [fetchHierarchy]
    );

    // ── After creation: hierarchy changes, use silent refresh ────────────────
    const handleCreated = async (newId: number) => {
      setCreateModalOpen(false);
      await fetchHierarchy(true);
      setSelectedId(newId);
    };

    // ── After import: hierarchy changes, use silent refresh ──────────────────
    const handleImported = async (rootIds: number[]) => {
      setImportModalOpen(false);
      await fetchHierarchy(true);
      if (rootIds.length > 0) {
        setSelectedId(rootIds[0]);
      }
      toast.showToast({
        type: "success",
        message: `${rootIds.length} asset${rootIds.length !== 1 ? "s" : ""} imported successfully.`,
      });
    };

    // ── Navigate to an asset (from chip / hierarchy nav) ─────────────────────
    const handleNavigate = (id: number) => {
      setSelectedId(id);
      // Expand ancestors so the selected node is visible in the tree
      const expandAncestors = (items: AssetTableItem[], targetId: number): boolean => {
        for (const item of items) {
          if (item.id === targetId) return true;
          if (item.children && expandAncestors(item.children, targetId)) {
            setExpandedIds((prev) => new Set([...prev, item.id]));
            return true;
          }
        }
        return false;
      };
      expandAncestors(assets, id);
    };

    // ── Reparent handler ──────────────────────────────────────────────────────
    const handleReparent = useCallback(
      async (assetId: number, newParentId: number | null) => {
        try {
          const res = await apiFetch(`/api/protected/assets/asset/${assetId}/parent`, {
            method: "PATCH",
            body: JSON.stringify({ parentId: newParentId ?? 0 }),
          });
          if (!res.ok) {
            const msg = await res.text();
            throw new Error(msg);
          }
          await fetchHierarchy(true); // structural change, but silent (no loading flash)
          if (newParentId !== null) {
            setExpandedIds((prev) => new Set([...prev, newParentId]));
          }
          toast.showToast({ type: "success", message: "Asset moved" });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Failed to move asset";
          toast.showToast({ type: "error", message: msg });
        } finally {
          setDraggedId(null);
          setDropTargetId(null);
          setDropOverRoot(false);
          setInvalidDropIds(new Set());
        }
      },
      [fetchHierarchy, toast]
    );

    // ── Drag event handlers ───────────────────────────────────────────────────
    const handleDragStart = useCallback(
      (id: number) => {
        setDraggedId(id);
        setInvalidDropIds(getSubtreeIds(assets, id));
      },
      [assets]
    );

    const handleDragEnd = useCallback(() => {
      setDraggedId(null);
      setDropTargetId(null);
      setDropOverRoot(false);
      setInvalidDropIds(new Set());
    }, []);

    const handleDragOver = useCallback((id: number) => {
      if (id === -1) {
        // -1 = root zone sentinel
        setDropOverRoot(true);
        setDropTargetId(null);
      } else {
        setDropOverRoot(false);
        setDropTargetId(id);
      }
    }, []);

    const handleDragLeave = useCallback((id: number) => {
      if (id === -1) setDropOverRoot(false);
      else setDropTargetId((prev) => (prev === id ? null : prev));
    }, []);

    const handleDrop = useCallback(
      (_targetId: number) => {
        if (draggedId === null) return;
        void handleReparent(draggedId, _targetId);
      },
      [draggedId, handleReparent]
    );

    const handleDropRoot = useCallback(() => {
      if (draggedId === null) return;
      void handleReparent(draggedId, null);
    }, [draggedId, handleReparent]);

    // ── Expose imperative handle to parent ───────────────────────────────────
    useImperativeHandle(ref, () => ({
      openAddRoot: handleAddRoot,
      openImport: () => setImportModalOpen(true),
      toggleSelectionMode: () => {
        setSelectionMode((prev) => !prev);
        setMultiSelectedIds(new Set());
      },
      getSelectedAssets: () => {
        return flatAssets.filter((a) => multiSelectedIds.has(a.id));
      },
      isSelectionMode: () => selectionMode,
    }));

    return (
      <div className="flex h-full overflow-hidden">
        {/* ── Left: Tree Panel (resizable on desktop, off-canvas drawer on mobile) ── */}
        <div
          className={`bg-zinc-900 flex flex-col flex-shrink-0 fixed inset-y-0 left-0 z-30 w-[85vw] max-w-xs transform transition-transform md:static md:z-auto md:w-auto md:max-w-none md:transform-none ${
            treeDrawerOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0`}
          style={
            isMobile
              ? { minHeight: 0, minWidth: 0 }
              : { width: sidebarWidth, minHeight: 0, minWidth: 0 }
          }
        >
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-xs">
              Loading assets…
            </div>
          ) : (
            <AssetTreePanel
              assets={assets}
              selectedId={selectedId}
              expandedIds={expandedIds}
              filterText={filterText}
              onSelect={handleSelect}
              onToggleExpand={handleToggleExpand}
              onFilterChange={handleFilterChange}
              onExpandAll={handleExpandAll}
              onCollapseAll={handleCollapseAll}
              onAddRoot={handleAddRoot}
              onAddChild={handleAddChild}
              onDelete={handleDelete}
              onImport={() => setImportModalOpen(true)}
              // Selection mode
              selectionMode={selectionMode}
              multiSelectedIds={multiSelectedIds}
              onToggleMultiSelect={handleToggleMultiSelect}
              // Drag and drop
              draggedId={draggedId}
              dropTargetId={dropTargetId}
              invalidDropIds={invalidDropIds}
              dropOverRoot={dropOverRoot}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDropRoot={handleDropRoot}
            />
          )}
        </div>

        {/* ── Mobile backdrop for the tree drawer ── */}
        {treeDrawerOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/50 md:hidden"
            onClick={() => setTreeDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* ── Resize handle (desktop only) ── */}
        <div
          onMouseDown={handleResizeStart}
          className="hidden md:block w-1 bg-zinc-800 hover:bg-amber-400/50 active:bg-amber-400/70 cursor-col-resize flex-shrink-0 transition-colors"
          style={{ minHeight: 0 }}
          title="Drag to resize"
        />

        {/* ── Right: Detail Panel (flex-1) ── */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
          <button
            type="button"
            onClick={() => setTreeDrawerOpen(true)}
            className="md:hidden flex items-center gap-2 px-3 py-2 text-sm text-amber-400 border-b border-zinc-800 bg-zinc-900"
            aria-label="Browse asset tree"
          >
            ☰ Browse tree
          </button>
          {detailLoading ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-xs">
              Loading details…
            </div>
          ) : (
            <AssetDetailPanel
              asset={selectedAsset}
              allAssets={flatAssets}
              hierarchyRoot={assets}
              onNavigate={handleNavigate}
              onAddChild={handleAddChild}
              onDelete={handleDelete}
              onRefreshDetail={handleRefreshDetail}
              onEdit={handleEdit}
            />
          )}
        </div>

        {/* ── Create Asset Modal ── */}
        <AssetCreateModal
          isOpen={createModalOpen}
          parentId={createModalParentId}
          allAssets={flatAssets}
          onClose={() => setCreateModalOpen(false)}
          onCreated={handleCreated}
        />

        {/* ── Import Assets Modal ── */}
        <AssetImportModal
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImported={handleImported}
        />
      </div>
    );
  }
);

export default AssetsExplorerView;
